import React from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronLeft,
  ArrowRight,
  Clock,
  Sparkles,
  Download as DownloadIcon,
} from 'lucide-react';
import SEOHead from '../components/SEOHead';
import Breadcrumbs from '../components/Breadcrumbs';

/**
 * Pain-point article (Phase 3) targeting the single most recurrent complaint
 * from demand mining: a messy Downloads folder. Maps to keywords
 * "keep downloads folder clean Mac" and "messy downloads folder mac". Five
 * strategy walkthrough (manual triage, native Stacks, Folder Actions, Hazel,
 * Sortio) so the post stays useful even for readers who do not buy Sortio.
 */

const PUBLISHED = '2026-05-14';
const CANONICAL = 'https://www.getsortio.com/blog/keep-mac-downloads-folder-clean';

const FAQ = [
  {
    question: 'How do I automatically organize my Downloads folder on Mac?',
    answer:
      'Three reliable options exist. macOS Folder Actions (free, native, fragile when system updates roll out), Hazel ($42 one-time, deterministic rules, brittle on content-aware tasks like reading a PDF), and Sortio (AI-driven, prompt-based, with a free tier of 10 sorts to test on a real folder). For most people the fastest path is to point Sortio at the Downloads folder, write a one-sentence prompt, and let it route files into the right places.',
  },
  {
    question: 'Why does my Mac Downloads folder get so messy?',
    answer:
      'Three forces. Default behaviour: every browser, every email app, every messaging app dumps files into Downloads with no further routing. Filename quality: the file is usually named whatever the server sent (statement.pdf, IMG_4839.jpg, document(2).pdf) so future-you cannot find it. Lack of habit: the folder is out of sight in the Dock, so the friction of opening Finder and triaging never wins against the friction of just leaving the file there.',
  },
  {
    question: 'Should I just delete everything in Downloads?',
    answer:
      'For most files, yes. Anything older than 60 days that you have not opened is safe to delete or archive. The exception is documents you might need later (invoices, receipts, tax forms, contracts). The point of this post is to set up a system where those exceptions are routed to a proper folder before they get lost in the noise. Then "delete everything older than 60 days" becomes a safe one-click cleanup.',
  },
  {
    question: 'Does Sortio rename files or just move them?',
    answer:
      'Both. Sortio can read the content of a PDF, image, or document and generate a meaningful filename: 2026-04-12_Vendor_Invoice_$142.83.pdf instead of statement.pdf. You write the template once (in plain English, in the prompt) and Sortio applies it per file. The rename happens before the move, so the file lands in the right folder with a name you can search by.',
  },
  {
    question: 'Does the Downloads folder cleanup run automatically or do I need to trigger it?',
    answer:
      'On the free tier, you run Sortio manually (from the menu bar, the Dock, or Apple Shortcuts) when the folder gets ugly. On Pro ($14.99/month or $99/year), watch folders run continuously: every new file that lands in Downloads gets sorted within seconds of arriving. For most users, manual on-demand is the right starting point.',
  },
  {
    question: 'What if I want files to leave my Mac in the cleanup?',
    answer:
      'Sortio supports local-only processing through Ollama, so files never leave the machine even when AI is reading them. This is the right pick for sensitive Downloads (medical records, financial statements, legal contracts). The trade-off is that local inference is slower and slightly less accurate than the managed cloud option, but it is fully functional and free of subscription tier limits.',
  },
];

const KeepDownloadsFolderCleanBlogPage = () => {
  const breadcrumbItems = [
    { label: 'Blog', href: null },
    { label: 'Keep your Mac Downloads folder clean', href: null },
  ];

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.getsortio.com/' },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: CANONICAL },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'Keep your Mac Downloads folder clean',
        item: CANONICAL,
      },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'How to Keep Your Mac Downloads Folder Clean (5 Strategies That Actually Work)',
    description:
      'Five practical strategies for keeping a Mac Downloads folder clean: triage, Stacks, Folder Actions, Hazel, and AI-driven sorting with Sortio. Real walkthrough plus an honest comparison.',
    url: CANONICAL,
    datePublished: PUBLISHED,
    dateModified: PUBLISHED,
    about: {
      '@type': 'SoftwareApplication',
      name: 'Sortio',
      url: 'https://www.getsortio.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'CMG Labs LLC',
      url: 'https://www.getsortio.com',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };

  return (
    <div className="min-h-screen bg-black text-apple-100">
      <SEOHead
        title="How to Keep Your Mac Downloads Folder Clean (5 Strategies)"
        description="Five practical ways to stop the Mac Downloads folder from becoming a wasteland. Manual triage, Stacks, Folder Actions, Hazel, and AI sorting compared, with a step-by-step Sortio setup."
        canonical={CANONICAL}
        schema={[breadcrumbSchema, articleSchema, faqSchema]}
        keywords="keep downloads folder clean mac, messy downloads folder mac, organize downloads folder mac, auto sort downloads mac, downloads folder automation"
        lastModified={PUBLISHED}
      />

      <div className="container mx-auto px-6 max-w-3xl py-16 lg:py-24">
        <Breadcrumbs items={breadcrumbItems} />

        <Link
          to="/articles"
          className="inline-flex items-center text-apple-300 hover:text-sortio-primary mb-8 transition-colors group"
        >
          <ChevronLeft className="w-4 h-4 mr-1 group-hover:-translate-x-1 transition-transform" />
          <span>All posts</span>
        </Link>

        <header className="mb-10">
          <span className="inline-block px-3 py-1 bg-sortio-primary/10 text-sortio-primary text-xs font-medium rounded-lg uppercase tracking-wider mb-4">
            Blog
          </span>
          <h1 className="text-4xl lg:text-5xl font-semibold text-apple-50 mb-6 leading-tight">
            How to Keep Your Mac Downloads Folder Clean (5 Strategies That Actually Work)
          </h1>
          <div className="flex items-center text-apple-400 text-sm gap-4">
            <span className="flex items-center">
              <Clock className="mr-2" size={14} />
              Published {new Date(PUBLISHED).toLocaleDateString()}
            </span>
            <span>9 min read</span>
          </div>
        </header>

        <article className="prose-content space-y-6 text-apple-200 leading-relaxed">
          <p className="text-lg">
            Open Finder, click Downloads, scroll. If your folder looks like most people&apos;s,
            you have somewhere between 800 and 9,000 files, most of them named things like
            statement(2).pdf, Screen Shot 2024-11-03 at 3.47.18 PM.png, and IMG_4839.HEIC.
            You cannot find anything in there, you do not want to throw it away, and every
            time you try to clean it up you give up halfway through. This is normal.
          </p>

          <p>
            The Downloads folder is the single most chaotic folder on a typical Mac. Every browser,
            every email client, every messenger writes here. Nothing reads from it. There is no
            built-in retention policy. And the friction of triage is just high enough that the
            folder gets worse every week until you eventually nuke it and start over.
          </p>

          <div className="bg-sortio-primary/10 border border-sortio-primary/20 rounded-2xl p-6 my-8 not-prose">
            <div className="flex items-start gap-3">
              <DownloadIcon className="text-sortio-primary flex-shrink-0 mt-0.5" size={20} />
              <div>
                <h3 className="font-medium text-apple-50 mb-2">The short version</h3>
                <p className="text-apple-300 text-sm leading-relaxed">
                  Decide what stays, where the keepers go, and how new files get routed before
                  they pile up. Five strategies range from free-and-manual (Stacks, occasional
                  triage) through deterministic-and-paid (Hazel) to AI-driven and prompt-based
                  (Sortio). Most people end up combining two: a one-time bulk sort to clear the
                  backlog, then a watch folder to keep it that way.
                </p>
              </div>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Why the Downloads folder fills up
          </h2>

          <p>
            Three things conspire to make Downloads a wasteland. First, every app that produces
            a file uses it as a default dumping ground. Browsers, mail clients, Messages, Slack,
            screenshot tools, AirDrop. Some you can reconfigure, most you do not bother. Second,
            filenames are whatever the source sent: statement.pdf, IMG_4839.jpg, file(2).pdf,
            attachment.zip. Future-you has no chance of finding the right file by name. Third,
            the folder is out of the way. It is in the Dock for downloads to land in, but Finder
            does not surface it the way it does the Desktop. The clutter is invisible until you
            go looking, which is why people only notice it when they need disk space.
          </p>

          <p>
            The fix is not "be more disciplined." Discipline does not survive a Tuesday afternoon
            when you are trying to find the wifi password your bank just emailed you. The fix is
            a system that routes new files automatically and keeps the routing rules simple
            enough that you trust them.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Strategy 1: Manual triage every Friday
          </h2>

          <p>
            The cheapest strategy and the one most people fail at. Set a recurring 15-minute
            calendar block on Friday afternoon. Open Downloads, sort by date added, work backwards.
            Anything that is not useful, delete. Anything that is, drag to its real home.
          </p>

          <p>
            This works for the first three weeks. Then it does not, because Friday afternoon is
            the worst possible time to ask anyone to do filing. The advantage is that it requires
            zero setup and costs nothing. The disadvantage is that you are the system, and you
            are the bottleneck.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Strategy 2: Stacks on the Desktop, accept Downloads as a junk drawer
          </h2>

          <p>
            macOS has had Stacks since Mojave. Right-click the Desktop, choose Use Stacks, and
            Finder will group everything by kind (Images, Documents, PDFs, etc.) on the Desktop.
            This does not touch the Downloads folder, but it solves a related problem: the Desktop
            stays visually clean even when you dump things on it.
          </p>

          <p>
            For Downloads itself, you can enable column or gallery view and sort by date added
            descending, which makes the recent files easy to find without organizing the rest.
            It is the minimal-viable-solution. You are not really organizing the folder, but you
            are admitting that it is a junk drawer and adjusting your expectations.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Strategy 3: macOS Folder Actions (free, fragile)
          </h2>

          <p>
            Folder Actions is a built-in AppleScript hook that fires whenever a file lands in a
            watched folder. With Automator you can build a workflow that, say, moves every PNG
            into a Screenshots folder and every PDF into Documents. It is free and it is native.
          </p>

          <p>
            It is also fragile. Folder Actions break across macOS upgrades surprisingly often.
            Permissions for the folder-action service have to be re-granted in System Settings.
            AppleScript itself has been a quasi-deprecated technology for a decade, and the
            documentation is mostly forum posts from 2014. If you already have a working Folder
            Action setup, do not throw it away. If you do not, do not start there in 2026.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Strategy 4: Hazel (deterministic, $42 one-time)
          </h2>

          <p>
            <Link to="/compare/hazel-alternative-mac" className="text-sortio-primary hover:underline">
              Hazel
            </Link>{' '}
            is the Mac power-user default. You define rules ("if filename starts with Screen Shot,
            move to ~/Pictures/Screenshots") and Hazel fires them whenever files arrive. For
            patterns that are stable (screenshots, installer .dmg files, ZIP archives) Hazel is
            outstanding. The rules run locally, no AI involved, and the failure mode is
            predictable: either the rule matched or it did not.
          </p>

          <p>
            Hazel struggles when the routing decision requires reading the file&apos;s contents.
            Bank statements are the canonical example: the filename is generic (statement.pdf)
            and the account number is inside the PDF, which means Hazel has to do content
            matching against the OCR text layer. That works on clean digitally generated PDFs
            and breaks on scanned PDFs where the OCR is noisy. We wrote a separate post about
            this:{' '}
            <Link to="/blog/hazel-content-matching-pdfs" className="text-sortio-primary hover:underline">
              why Hazel content matching breaks on real-world PDFs
            </Link>
            .
          </p>

          <p>
            If your Downloads folder is mostly extension-based or filename-pattern-based, Hazel
            is a great choice and the $42 lifetime price is fair. If a meaningful chunk of your
            files need to be routed by content, you will want an AI layer on top.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Strategy 5: Sortio (AI, prompt-based, free tier to test)
          </h2>

          <p>
            Sortio is built around two ideas. First, you describe what you want in plain English
            instead of writing rules ("sort my Downloads folder. Screenshots go to ~/Pictures/Screenshots.
            Invoices and receipts go to ~/Documents/Finance/2026, renamed by vendor and date.
            Installers go to the Trash. Everything else goes to ~/Documents/Inbox"). Second,
            Sortio reads the file&apos;s content, not just its filename, so an invoice that the
            sender named "scan.pdf" still gets routed correctly.
          </p>

          <p>
            For the Downloads folder specifically, the typical setup is a one-time bulk sort to
            clear the backlog and then a watch folder (Pro feature) that catches every new file
            within seconds of it arriving. The free tier includes a one-time AI trial allowance, which is
            usually enough to test on the existing mess and see if the output is what you wanted
            before subscribing.
          </p>

          <h3 className="text-xl font-medium text-apple-50 mt-8 mb-3">
            Setting up Sortio on your Downloads folder
          </h3>

          <p>
            The end-to-end flow is short. Download Sortio from{' '}
            <Link to="/download" className="text-sortio-primary hover:underline">
              getsortio.com/download
            </Link>
            . Sign in (or stay on the free tier). Click New Sort, point it at ~/Downloads, and
            write a prompt that describes how you want files routed. A first prompt that works
            for most people:
          </p>

          <pre className="bg-apple-700/30 rounded-lg p-4 overflow-x-auto text-sm text-apple-100 not-prose">
{`Sort the Downloads folder.

Screenshots (PNG files that start with "Screen Shot" or "CleanShot")
go to ~/Pictures/Screenshots/{year}/.

Invoices and receipts (PDFs that contain a total amount and a
vendor name) go to ~/Documents/Finance/{year}/, renamed to
{date}_{vendor}_{amount}.pdf.

Bank statements (PDFs that contain "Statement Period") go to
~/Documents/Finance/Statements/{year}/, renamed to
{year}_{month}_{bank}.pdf.

App installers (.dmg, .pkg) go to the Trash. They are one-time
files.

ZIP files older than 30 days go to ~/Downloads/_archive/.

Everything else goes to ~/Documents/Inbox/ untouched.`}
          </pre>

          <p>
            Click Preview. Sortio shows you exactly which files are going where, with no actual
            moves yet. You can override any individual decision before committing. If the preview
            looks right, click Apply. If it does not, edit the prompt and re-run; AI sort takes
            seconds even on a few thousand files.
          </p>

          <p>
            Once the bulk sort is clean, the next step is to promote the routing into a watch
            folder so new files coming into Downloads get sorted automatically. On Pro this is a
            one-click toggle: Watch this folder, run the same prompt on every new arrival.
            Within a week the Downloads folder is back to empty by default, because every file
            that arrives has somewhere to go.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            How to pick between the five strategies
          </h2>

          <p>
            A quick decision tree. If your Downloads folder is small (under 200 files) and your
            patterns are simple (move PDFs to Documents, screenshots to Pictures), Folder Actions
            or even manual triage is enough. If your patterns are stable but you want them to run
            without thinking, Hazel is the right tool. If a meaningful chunk of your files need
            content-aware routing (invoices, statements, receipts that arrive with generic
            filenames), Sortio AI sort is what you want. If you have a few thousand files of
            backlog right now, run Sortio once on the existing mess to clear it, then decide
            whether you want the watch-folder automation on top.
          </p>

          <p>
            Most heavy users end up running both Hazel and Sortio. Hazel handles the deterministic
            patterns (screenshots, installers, ZIPs) and Sortio takes the content-aware cases that
            were always brittle in Hazel. The two tools coexist cleanly: Sortio watches the
            Downloads folder, Hazel watches specific subfolders for things like
            "rename this PDF by date and move to Archive."
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            A note on retention
          </h2>

          <p>
            A clean Downloads folder is not the same as a permanent Downloads folder. Most files
            that land here are ephemeral: one-time installers, transient screenshots, a draft PDF
            someone emailed for review. Build a retention policy into your sort: anything older
            than 30 or 60 days that has not been opened goes to an archive subfolder or to the
            Trash. Sortio can include "delete files older than 60 days where last opened is null"
            in the prompt, and Hazel has a similar action. The point is that the Downloads folder
            should approach zero by default, not collect every file you have ever downloaded
            forever.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            FAQ
          </h2>

          <div className="space-y-4 not-prose">
            {FAQ.map((f, i) => (
              <div key={i} className="bg-apple-700/20 rounded-xl p-6">
                <h3 className="font-medium text-apple-50 mb-3">{f.question}</h3>
                <p className="text-apple-300 leading-relaxed">{f.answer}</p>
              </div>
            ))}
          </div>
        </article>

        {/* Internal links */}
        <section className="mt-16 pt-8 border-t border-apple-700/30">
          <h2 className="text-xl font-semibold text-apple-50 mb-4">Keep reading</h2>
          <div className="grid gap-3">
            <Link
              to="/blog/automatically-rename-pdfs-by-content"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                Automatically rename PDFs by content (invoices, receipts, statements)
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
            <Link
              to="/blog/tame-screenshot-chaos-mac"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                Tame screenshot chaos on Mac
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
            <Link
              to="/compare/hazel-alternative-mac"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                Hazel alternatives for Mac (the full roundup)
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
          </div>
        </section>

        <section className="mt-12">
          <div className="bg-gradient-to-r from-sortio-primary/10 to-sortio-secondary/10 rounded-2xl p-8 border border-sortio-primary/20">
            <div className="flex items-center mb-4">
              <Sparkles className="text-sortio-primary mr-3" size={24} />
              <h2 className="text-2xl font-semibold text-apple-50">Clear your Downloads folder in 10 minutes</h2>
            </div>
            <p className="text-apple-300 mb-6 leading-relaxed">
              The free tier includes a one-time AI trial allowance, enough to clear the backlog in your
              Downloads folder and decide whether watch folders are worth the Pro upgrade. No
              credit card required.
            </p>
            <Link
              to="/download"
              className="inline-flex items-center px-6 py-3 bg-sortio-primary hover:bg-sortio-primary/90 text-white font-medium rounded-xl transition-colors duration-200"
            >
              Download Sortio
              <ArrowRight className="ml-2" size={16} />
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default KeepDownloadsFolderCleanBlogPage;
